/**
 * Auto Repair Booking Page - Deep-link enabled
 * Routes like /auto-repair/tires open directly to booking with preselected auto repair subcategory
 */
import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import BookingForm from '@/components/BookingForm';
import { GlobalNav } from '@/components/GlobalNav';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Wrench, Car, Settings, CircleDot, Cog, Droplet, 
  Gauge, Zap, Thermometer, Wind, CircleSlash, CheckCircle, Shield, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Subcategory slug to ID mapping
const SUBCATEGORY_SLUGS = {
  'tires': 'tires',
  'rehvid': 'tires',
  'renkaat': 'tires',
  'diagnostics': 'diagnostics',
  'diagnostika': 'diagnostics',
  'engine': 'engine',
  'mootor': 'engine',
  'moottori': 'engine',
  'bodywork': 'bodywork',
  'keretööd': 'bodywork',
  'brakes': 'brakes',
  'pidurid': 'brakes',
  'jarrut': 'brakes',
  'oil-change': 'oil_change',
  'oil_change': 'oil_change',
  'õlivahetus': 'oil_change',
  'transmission': 'transmission',
  'käigukast': 'transmission',
  'electrical': 'electrical',
  'elektriline': 'electrical',
  'ac-heating': 'ac_heating',
  'ac_heating': 'ac_heating',
  'konditsioneer': 'ac_heating',
  'exhaust': 'exhaust',
  'heitgaas': 'exhaust',
  'suspension': 'suspension',
  'vedrustus': 'suspension',
  'general-repair': 'general_repair',
  'general_repair': 'general_repair',
  'üldremont': 'general_repair'
};

// Subcategory display info
const SUBCATEGORY_INFO = {
  'tires': {
    icon: CircleDot,
    label: { en: 'Tire Services', et: 'Rehviteenus', fi: 'Rengaspalvelut', nb: 'Dekkservice', ru: 'Шиномонтаж', sv: 'Däckservice' },
    desc: { en: 'Tire change, repair, and balancing', et: 'Rehvivahetus, remont ja tasakaalustus', fi: 'Renkaanvaihto, korjaus ja tasapainotus', nb: 'Dekkskifte, reparasjon og balansering', ru: 'Замена, ремонт и балансировка шин', sv: 'Däckbyte, reparation och balansering' }
  },
  'diagnostics': {
    icon: Gauge,
    label: { en: 'Diagnostics', et: 'Diagnostika', fi: 'Diagnostiikka', nb: 'Feilsøking', ru: 'Диагностика', sv: 'Felsökning' },
    desc: { en: 'Computer diagnostics and fault finding', et: 'Arvutidiagnostika ja veaotsing', fi: 'Tietokonediagnostiikka ja vikojen etsintä', nb: 'Datadiagnostikk og feilsøking', ru: 'Компьютерная диагностика и поиск неисправностей', sv: 'Datordiagnostik och felsökning' }
  },
  'engine': {
    icon: Cog,
    label: { en: 'Engine Repair', et: 'Mootori remont', fi: 'Moottorin korjaus', nb: 'Motorreparasjon', ru: 'Ремонт двигателя', sv: 'Motorreparation' },
    desc: { en: 'Engine repair and maintenance', et: 'Mootori remont ja hooldus', fi: 'Moottorin korjaus ja huolto', nb: 'Motorreparasjon og vedlikehold', ru: 'Ремонт и обслуживание двигателя', sv: 'Motorreparation och underhåll' }
  },
  'bodywork': {
    icon: Car,
    label: { en: 'Bodywork', et: 'Keretööd', fi: 'Korityöt', nb: 'Karosseriarbeid', ru: 'Кузовные работы', sv: 'Karosseriarbete' },
    desc: { en: 'Dent repair and body restoration', et: 'Mõlkide remont ja kere taastamine', fi: 'Kolhunkorjaus ja korin korjaus', nb: 'Bulkereparasjon og karosseri-restaurering', ru: 'Ремонт вмятин и восстановление кузова', sv: 'Buckreparation och karosseriåterställning' }
  },
  'brakes': {
    icon: CircleSlash,
    label: { en: 'Brake Services', et: 'Piduriteenus', fi: 'Jarrupalvelut', nb: 'Bremseservice', ru: 'Тормозные услуги', sv: 'Bromsservice' },
    desc: { en: 'Brake pads, discs, and system repair', et: 'Piduriklotsid, kettad ja süsteemi remont', fi: 'Jarrupalat, levyt ja järjestelmän korjaus', nb: 'Bremseklosser, skiver og systemreparasjon', ru: 'Тормозные колодки, диски и ремонт системы', sv: 'Bromsbelägg, skivor och systemreparation' }
  },
  'oil_change': {
    icon: Droplet,
    label: { en: 'Oil Change', et: 'Õlivahetus', fi: 'Öljynvaihto', nb: 'Oljeskift', ru: 'Замена масла', sv: 'Oljebyte' },
    desc: { en: 'Oil and filter replacement', et: 'Õli ja filtri vahetus', fi: 'Öljyn ja suodattimen vaihto', nb: 'Olje- og filterskift', ru: 'Замена масла и фильтра', sv: 'Olje- och filterbyte' }
  },
  'transmission': {
    icon: Settings,
    label: { en: 'Transmission', et: 'Käigukast', fi: 'Vaihteisto', nb: 'Girkasse', ru: 'Коробка передач', sv: 'Växellåda' },
    desc: { en: 'Gearbox repair and service', et: 'Käigukasti remont ja hooldus', fi: 'Vaihteiston korjaus ja huolto', nb: 'Girkassereparasjon og service', ru: 'Ремонт и обслуживание коробки передач', sv: 'Växellådsreparation och service' }
  },
  'electrical': {
    icon: Zap,
    label: { en: 'Electrical', et: 'Elektritööd', fi: 'Sähkötyöt', nb: 'Elektrisk', ru: 'Электрика', sv: 'El-arbeten' },
    desc: { en: 'Electrical system diagnosis and repair', et: 'Elektrisüsteemi diagnoos ja remont', fi: 'Sähköjärjestelmän diagnoosi ja korjaus', nb: 'Diagnose og reparasjon av elektrisk system', ru: 'Диагностика и ремонт электросистемы', sv: 'Diagnos och reparation av elsystem' }
  },
  'ac_heating': {
    icon: Thermometer,
    label: { en: 'AC & Heating', et: 'Kliima ja küte', fi: 'Ilmastointi ja lämmitys', nb: 'AC og varme', ru: 'Кондиционер и отопление', sv: 'AC och värme' },
    desc: { en: 'Air conditioning and heating service', et: 'Kliimaseadme ja küttesüsteemi hooldus', fi: 'Ilmastoinnin ja lämmityksen huolto', nb: 'Service for klimaanlegg og varme', ru: 'Обслуживание кондиционера и отопления', sv: 'Service av klimatanläggning och värme' }
  },
  'exhaust': {
    icon: Wind,
    label: { en: 'Exhaust System', et: 'Heitgaasisüsteem', fi: 'Pakoputkisto', nb: 'Eksosanlegg', ru: 'Выхлопная система', sv: 'Avgassystem' },
    desc: { en: 'Exhaust repair and replacement', et: 'Heitgaasisüsteemi remont ja vahetus', fi: 'Pakoputkiston korjaus ja vaihto', nb: 'Reparasjon og bytte av eksosanlegg', ru: 'Ремонт и замена выхлопной системы', sv: 'Reparation och byte av avgassystem' }
  },
  'suspension': {
    icon: Car,
    label: { en: 'Suspension', et: 'Vedrustus', fi: 'Jousitus', nb: 'Hjuloppheng', ru: 'Подвеска', sv: 'Hjulupphängning' },
    desc: { en: 'Shock absorbers and suspension work', et: 'Amortisaatorid ja vedrustustööd', fi: 'Iskunvaimentimet ja jousitustyöt', nb: 'Støtdempere og opphengsarbeid', ru: 'Амортизаторы и работы по подвеске', sv: 'Stötdämpare och hjulupphängningsarbete' }
  },
  'general_repair': {
    icon: Wrench,
    label: { en: 'General Repair', et: 'Üldremont', fi: 'Yleiskorjaus', nb: 'Generell reparasjon', ru: 'Общий ремонт', sv: 'Allmän reparation' },
    desc: { en: 'General maintenance and repairs', et: 'Üldine hooldus ja remont', fi: 'Yleinen huolto ja korjaus', nb: 'Generelt vedlikehold og reparasjoner', ru: 'Общее обслуживание и ремонт', sv: 'Allmänt underhåll och reparationer' }
  }
};

const AutoRepairBookingPage = () => {
  const { subcategory: subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  
  // Resolve subcategory from slug or URL param
  const subcategoryId = SUBCATEGORY_SLUGS[subcategorySlug] || searchParams.get('subcategory') || null;
  const subcategoryInfo = subcategoryId ? SUBCATEGORY_INFO[subcategoryId] : null;
  const Icon = subcategoryInfo?.icon || Wrench;

  // Labels for the page
  const labels = {
    en: {
      title: subcategoryInfo ? subcategoryInfo.label.en : 'Auto Repair Booking',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.en : 'Request auto repair services',
      backToAuto: 'Back to Auto Services',
      features: ['Certified mechanics', 'Transparent pricing', 'Quality parts', 'Warranty included']
    },
    et: {
      title: subcategoryInfo ? subcategoryInfo.label.et : 'Autoremondi broneerimine',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.et : 'Taotle autoremonditeenust',
      backToAuto: 'Tagasi autoteenuste juurde',
      features: ['Sertifitseeritud mehaanikud', 'Läbipaistev hinnastus', 'Kvaliteetsed osad', 'Garantii kaasas']
    },
    fi: {
      title: subcategoryInfo ? subcategoryInfo.label.fi : 'Autokorjauksen varaus',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.fi : 'Pyydä autokorjauspalvelua',
      backToAuto: 'Takaisin autopalveluihin',
      features: ['Sertifioidut mekaanikot', 'Läpinäkyvä hinnoittelu', 'Laadukkaat osat', 'Takuu mukana']
    },
    nb: {
      title: subcategoryInfo ? (subcategoryInfo.label.nb || subcategoryInfo.label.en) : 'Bilverksted-bestilling',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.nb || subcategoryInfo.desc.en) : 'Be om verkstedtjenester',
      backToAuto: 'Tilbake til biltjenester',
      features: ['Sertifiserte mekanikere', 'Transparent prising', 'Kvalitetsdeler', 'Garanti inkludert']
    },
    ru: {
      title: subcategoryInfo ? (subcategoryInfo.label.ru || subcategoryInfo.label.en) : 'Запись в автосервис',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.ru || subcategoryInfo.desc.en) : 'Запрос услуг автосервиса',
      backToAuto: 'Назад к автоуслугам',
      features: ['Сертифицированные механики', 'Прозрачные цены', 'Качественные детали', 'Гарантия включена']
    },
    sv: {
      title: subcategoryInfo ? (subcategoryInfo.label.sv || subcategoryInfo.label.en) : 'Bilverkstadsbokning',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.sv || subcategoryInfo.desc.en) : 'Be om bilverkstadstjänster',
      backToAuto: 'Tillbaka till biltjänster',
      features: ['Certifierade mekaniker', 'Transparent prissättning', 'Kvalitetsdelar', 'Garanti ingår']
    }
  };

  const l = labels[language] || labels.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-slate-50">
      <GlobalNav />
      
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link to="/auto" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" />
          {l.backToAuto}
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: Info Section */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-orange-100 text-orange-700">
                <Icon className="h-3.5 w-3.5 mr-1" />
                {language === 'et' ? 'Autoremont'
                  : language === 'fi' ? 'Autokorjaamo'
                  : language === 'nb' ? 'Bilverksted'
                  : language === 'ru' ? 'Автосервис'
                  : language === 'sv' ? 'Bilverkstad'
                  : 'Auto Repair'}
              </Badge>
              
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="auto-repair-booking-title">
                {l.title}
              </h1>
              
              <p className="text-lg text-slate-600 max-w-lg" data-testid="auto-repair-booking-subtitle">
                {l.subtitle}
              </p>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {l.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-sm border text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </motion.div>

            {/* Trust Banner */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-gradient-to-r from-orange-600 to-orange-800 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Shield className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">
                        {language === 'et' ? 'Sertifitseeritud ja garanteeritud' : language === 'fi' ? 'Sertifioitu ja takuulla' : 'Certified & Guaranteed'}
                      </h3>
                      <p className="text-orange-100 text-sm">
                        {language === 'et' ? 'Kõik mehaanikud on sertifitseeritud, töödele kehtib garantii' : language === 'fi' ? 'Kaikki mekaanikot ovat sertifioituja, töille on takuu' : 'All mechanics are certified with warranty on work'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right: Booking Form with preselection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <BookingForm 
              defaultCategory="auto-repair"
              defaultSubcategory={subcategoryId}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AutoRepairBookingPage;
